import { useState, useEffect, useCallback } from 'react';
import { getSupabase } from '@/lib/supabase';
import {
  getClientActivityLog,
  getCoachActivityFeed,
  getClientActivityByType,
  getClientActivityStats,
  getCoachActivitySummary,
  type ActivityEvent,
  type ActivityEventWithClient,
} from '@/data/activityEvents';
import { reportSwallowed } from '@/lib/sentry';
import { toast } from 'sonner';

/**
 * Hook for client-specific activity log with real-time updates
 */
export function useClientActivityLog(clientId: string, days: number = 7, limit: number = 50) {
  const [activities, setActivities] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load initial data
  const loadActivities = useCallback(async () => {
    if (!clientId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getClientActivityLog(clientId, days, limit);
      setActivities(data);
    } catch (err) {
      console.error('Failed to load client activities:', err);
      setError('Failed to load activities');
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, days, limit]);

  // Set up real-time subscription
  useEffect(() => {
    if (!clientId) return;

    loadActivities();

    const sb = getSupabase();
    if (!sb) return;

    const channel = sb
      .channel(`client-activity-${clientId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'client_events',
          filter: `client_id=eq.${clientId}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            // Only add activities where COACH is the actor (for client to see)
            const newEvent = payload.new as ActivityEvent;
            if (newEvent.actor_id === newEvent.coach_id) {
              setActivities((prev) => [newEvent, ...prev.slice(0, limit - 1)]);
            }
          } else if (payload.eventType === 'UPDATE') {
            // Update existing activity (only if it's a coach activity)
            const updatedEvent = payload.new as ActivityEvent;
            if (updatedEvent.actor_id === updatedEvent.coach_id) {
              setActivities((prev) =>
                prev.map((activity) => (activity.id === updatedEvent.id ? updatedEvent : activity)),
              );
            }
          } else if (payload.eventType === 'DELETE') {
            // Remove deleted activity
            setActivities((prev) => prev.filter((activity) => activity.id !== payload.old.id));
          }
        },
      )
      .subscribe();

    return () => {
      sb.removeChannel(channel);
    };
  }, [clientId, days, limit, loadActivities]);

  // Refresh function
  const refresh = useCallback(() => {
    loadActivities();
  }, [loadActivities]);

  return {
    activities,
    loading,
    error,
    refresh,
  };
}

/**
 * Hook for coach activity feed with real-time updates
 */
export function useCoachActivityFeed(coachId: string, days: number = 30, limit: number = 100) {
  const [activities, setActivities] = useState<ActivityEventWithClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load initial data
  const loadActivities = useCallback(async () => {
    if (!coachId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getCoachActivityFeed(coachId, days, limit);
      setActivities(data);
    } catch (err) {
      console.error('Failed to load coach activities:', err);
      setError('Failed to load activities');
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [coachId, days, limit]);

  // Set up real-time subscription
  useEffect(() => {
    if (!coachId) return;

    loadActivities();

    const sb = getSupabase();
    if (!sb) return;

    const channel = sb
      .channel(`coach-activity-${coachId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'client_events',
          filter: `coach_id=eq.${coachId}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            // Only add activities where CLIENT is the actor (for coach to see)
            const newEvent = payload.new as ActivityEvent;
            if (newEvent.actor_id === newEvent.client_id) {
              const newActivity = newEvent as ActivityEventWithClient;
              newActivity.client_name = 'Loading...';
              newActivity.client_initials = 'L';
              setActivities((prev) => [newActivity, ...prev.slice(0, limit - 1)]);

              // Optionally refresh to get complete data with client info
              setTimeout(() => loadActivities(), 1000);
            }
          } else if (payload.eventType === 'UPDATE') {
            // Update existing activity (only if it's a client activity)
            const updatedEvent = payload.new as ActivityEvent;
            if (updatedEvent.actor_id === updatedEvent.client_id) {
              setActivities((prev) =>
                prev.map((activity) => (activity.id === updatedEvent.id ? { ...activity, ...updatedEvent } : activity)),
              );
            }
          } else if (payload.eventType === 'DELETE') {
            setActivities((prev) => prev.filter((activity) => activity.id !== payload.old.id));
          }
        },
      )
      .subscribe();

    return () => {
      sb.removeChannel(channel);
    };
  }, [coachId, days, limit, loadActivities]);

  // Refresh function
  const refresh = useCallback(() => {
    loadActivities();
  }, [loadActivities]);

  return {
    activities,
    loading,
    error,
    refresh,
  };
}

/**
 * Hook for filtered activity by type
 */
export function useActivityByType(clientId: string, types: string[], days: number = 7, limit: number = 50) {
  const [activities, setActivities] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadActivities = useCallback(async () => {
    if (!clientId || types.length === 0) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getClientActivityByType(clientId, types, days, limit);
      setActivities(data);
    } catch (err) {
      console.error('Failed to load filtered activities:', err);
      setError('Failed to load activities');
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, types, days, limit]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  return {
    activities,
    loading,
    error,
    refresh: loadActivities,
  };
}

/**
 * Hook for client activity statistics
 */
export function useClientActivityStats(clientId: string, days: number = 7) {
  const [stats, setStats] = useState({
    total: 0,
    workoutsCompleted: 0,
    workoutsMissed: 0,
    trackersLogged: 0,
    goalsUpdated: 0,
    messagesReceived: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStats = useCallback(async () => {
    if (!clientId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getClientActivityStats(clientId, days);
      setStats(data);
    } catch (err) {
      console.error('Failed to load activity stats:', err);
      setError('Failed to load stats');
    } finally {
      setLoading(false);
    }
  }, [clientId, days]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return {
    stats,
    loading,
    error,
    refresh: loadStats,
  };
}

/**
 * Hook for coach activity summary
 */
export function useCoachActivitySummary(coachId: string, days: number = 7) {
  const [summary, setSummary] = useState({
    totalActivities: 0,
    activeClients: 0,
    workoutsCompleted: 0,
    missedWorkouts: 0,
    newMessages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    if (!coachId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getCoachActivitySummary(coachId, days);
      setSummary(data);
    } catch (err) {
      console.error('Failed to load activity summary:', err);
      setError('Failed to load summary');
    } finally {
      setLoading(false);
    }
  }, [coachId, days]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  return {
    summary,
    loading,
    error,
    refresh: loadSummary,
  };
}

/**
 * Hook for paginated activity feed
 */
export function usePaginatedActivityFeed(clientId?: string, coachId?: string, pageSize: number = 20) {
  const [activities, setActivities] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;

    try {
      setLoading(true);

      let newActivities: ActivityEvent[] = [];
      if (clientId) {
        newActivities = await getClientActivityLog(clientId, 365, pageSize); // Large days range for pagination
      } else if (coachId) {
        const data = await getCoachActivityFeed(coachId, 365, pageSize);
        newActivities = data;
      }

      // Simple pagination by skipping already loaded items
      const startIndex = page * pageSize;
      const pageData = newActivities.slice(startIndex, startIndex + pageSize);

      if (pageData.length === 0) {
        setHasMore(false);
      } else {
        setActivities((prev) => [...prev, ...pageData]);
        setPage((prev) => prev + 1);
      }
    } catch (err) {
      console.error('Failed to load more activities:', err);
      // TODO(component-tests): (renderHook) loadMore fails → toast + report, loading resets, page unchanged. Needs React Testing Library — see the component-testing ticket.
      reportSwallowed('useActivityFeed.loadMore', err);
      toast.error("Couldn't load more activity. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [clientId, coachId, page, pageSize, loading, hasMore]);

  const reset = useCallback(() => {
    setActivities([]);
    setPage(0);
    setHasMore(true);
  }, []);

  return {
    activities,
    loading,
    hasMore,
    loadMore,
    reset,
  };
}
